import React, { useState } from "react";
import { Outlet } from "react-router-dom";
import { AlertTriangle, History, RefreshCw, Save } from "lucide-react";
import SettingsHistoryDrawer from "../../../components/admin/SettingsHistoryDrawer";
import { usePermission } from "../../../hooks/usePermission";
import { PageHeader } from "../_shared";
import AssetContextPreviewModal from "../../../components/admin/AssetContextPreviewModal";
import ImageEditModal from "../../../components/admin/ImageEditModal";
import MediaPickerModal from "../MediaPickerModal";
import WebsiteHeaderPreviewModal from "../../../components/admin/WebsiteHeaderPreviewModal";
import FooterPreviewModal from "../../../components/admin/FooterPreviewModal";
import PagePreviewModal from "../../../components/admin/PagePreviewModal";
import AboutPage from "../../About";
import { guessMimeFromExt } from "./adminSettingsDefaults";
import { SETTINGS_SECTION_HEAD } from "./adminSettingsConstants";
import { useAdminSettings } from "./AdminSettingsContext";

export default function AdminSettingsShell() {
  const ctx = useAdminSettings();
  const { ToastPortal } = ctx;
  const { can } = usePermission();
  const [historyOpen, setHistoryOpen] = useState(false);
  const canEdit = can("settings:write");

  if (ctx.loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-9 w-9 animate-spin rounded-full border-[3px] border-admin-muted border-t-admin-primary" />
          <p className="text-sm font-medium text-admin-textMuted">Đang tải cài đặt...</p>
        </div>
      </div>
    );
  }

  if (ctx.loadError) {
    return (
      <div className="mx-auto max-w-xl px-4 py-16">
        <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-6 text-center">
          <AlertTriangle className="mx-auto h-8 w-8 text-red-600" />
          <h2 className="mt-3 text-base font-bold text-red-900">Không tải được cài đặt hiện tại</h2>
          <p className="mt-1 text-sm text-red-800">
            {ctx.loadError}. Để tránh ghi đè logo, SEO và nội dung trang bằng dữ liệu trống, chức năng lưu tạm thời bị khoá.
          </p>
          <button
            type="button"
            onClick={ctx.retryLoadSettings}
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-admin-primary px-4 py-2 text-sm font-bold text-white shadow-sm transition hover:bg-admin-primaryHover"
          >
            <RefreshCw className="h-4 w-4" />
            Thử lại
          </button>
        </div>
        <ToastPortal />
      </div>
    );
  }

  const sectionHead =
    SETTINGS_SECTION_HEAD[ctx.activeSection] || SETTINGS_SECTION_HEAD.global;

  return (
    <>
      <PageHeader
        title={sectionHead.title}
        subtitle={sectionHead.subtitle}
        action={
          <button
            type="button"
            onClick={() => setHistoryOpen(true)}
            className="inline-flex h-10 items-center gap-2 rounded-lg border border-admin-border bg-admin-surface px-4 text-sm font-semibold text-admin-text shadow-sm transition hover:bg-admin-muted"
          >
            <History size={16} />
            Lịch sử phiên bản
          </button>
        }
      />
      {!canEdit ? (
        <p className="mx-auto mb-4 max-w-7xl rounded-lg bg-amber-50 px-4 py-2.5 text-sm text-amber-900 sm:mx-6 lg:mx-8">
          Vai trò của bạn chỉ được xem cài đặt. Liên hệ chủ sở hữu để thay đổi.
        </p>
      ) : null}

      <div className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8">
        <Outlet />
        <div className="fixed bottom-0 left-0 right-0 z-50 border-t-2 border-admin-border bg-admin-surface p-4 shadow-soft-xl">
          <div className="mx-auto flex max-w-7xl items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-sm font-medium text-admin-textMuted">
              <span className="h-2 w-2 rounded-full bg-amber-400 ring-2 ring-amber-100" />
              Có thay đổi chưa lưu
            </div>
            <button
              type="button"
              onClick={ctx.handleSave}
              disabled={ctx.saving || !canEdit || ctx.loading || Boolean(ctx.loadError)}
              title={canEdit ? undefined : "Bạn không có quyền sửa cài đặt"}
              className="flex items-center gap-2 rounded-xl bg-admin-primary px-5 py-2.5 text-sm font-black text-white shadow-sm transition hover:bg-admin-primaryHover disabled:opacity-50"
              style={{ boxShadow: "0 1px 0 rgba(0,0,0,0.18) inset, 0 3px 10px rgba(37,106,191,0.28)" }}
            >
              <Save className="h-4 w-4" />
              {ctx.saving ? "Đang lưu..." : "Lưu cài đặt"}
            </button>
          </div>
        </div>
      </div>

      <SettingsHistoryDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        onRestored={(v) => {
          ctx.toast(`Đã khôi phục về phiên bản ${v}`, "success", { duration: 3000 });
          ctx.reloadSettings();
        }}
      />

      {ctx.showAssetPreview && (
        <AssetContextPreviewModal
          field={ctx.showAssetPreview}
          data={ctx.previewData}
          onClose={() => ctx.setShowAssetPreview(null)}
        />
      )}

      {ctx.showImageEdit && (
        <ImageEditModal
          title={ctx.showImageEdit.title}
          file={ctx.showImageEdit.file}
          shape={ctx.showImageEdit.shape}
          onCancel={() => ctx.setShowImageEdit(null)}
          onConfirm={async (processedFile) => {
            const field = ctx.showImageEdit.field;
            ctx.setShowImageEdit(null);
            await ctx.handleAssetUpload(field, processedFile);
          }}
        />
      )}

      {ctx.showMediaPicker && (
        <MediaPickerModal
          open
          title={ctx.showMediaPicker?.title || "Chọn ảnh"}
          initialPrefix={ctx.showMediaPicker?.initialPrefix || "site/"}
          accept={ctx.showMediaPicker?.accept || "any"}
          onSelect={(file) => {
            const path = String(ctx.showMediaPicker?.path || "").trim();
            if (path) {
              ctx.updateField(path, {
                key: file?.key || "",
                url: file?.url || "",
                mimeType: file?.mimeType || guessMimeFromExt(file?.ext) || "",
                size: Number(file?.size || 0),
              });
            }
            ctx.setShowMediaPicker(null);
          }}
          onClose={() => ctx.setShowMediaPicker(null)}
        />
      )}

      {ctx.showWebsitePreview && (
        <WebsiteHeaderPreviewModal
          data={ctx.previewData}
          onClose={() => ctx.setShowWebsitePreview(false)}
        />
      )}

      {ctx.showFooterPreview && (
        <FooterPreviewModal
          data={ctx.previewData}
          onClose={() => ctx.setShowFooterPreview(false)}
        />
      )}

      {ctx.showPagePreview && (
        <PagePreviewModal
          title={ctx.showPagePreview.title}
          routePath={ctx.showPagePreview.routePath}
          data={ctx.data}
          scrollToId={ctx.showPagePreview.scrollToId}
          onClose={() => ctx.setShowPagePreview(null)}
        >
          {ctx.showPagePreview.page === "about" && <AboutPage />}
        </PagePreviewModal>
      )}

      <ToastPortal />
    </>
  );
}
